import React, {useCallback} from 'react'
import {
  DayButton,
  DayButtonProps,
  DayPicker,
  Formatters,
  Matcher,
  MonthCaption,
  MonthCaptionProps,
  NextMonthButton,
  NextMonthButtonProps,
  PreviousMonthButton,
  PreviousMonthButtonProps,
  Weekday,
  WeekdayProps
} from 'react-day-picker'
import {cx} from '@emotion/css'
import {addMonths} from 'date-fns/addMonths'
import {isSameDay} from 'date-fns/isSameDay'
import {isSameMonth} from 'date-fns/isSameMonth'
import {enUS} from 'date-fns/locale/en-US'
import {subDays} from 'date-fns/subDays'

import {Icon} from '@vio/atlas/Icon'
import {AvailabilityHotelEntity} from '@vio/core/src/availability/types'
import {WEEKENDS_INDEXES} from '@vio/core/src/datePicker/config'
import {
  dateFormat,
  dateStringToMiddayDate,
  dateToMiddayDate,
  MiddayDate,
  UTS_DATE_FORMAT
} from '@vio/core/src/utils/date'

import {MonthName} from '../components'
import {ColorPriceDayInner} from '../components/ColorPriceDayInner'
import {AvailabilityDatePickerContainer} from './styles'

export type DatePickerVariant = 'vertical' | 'horizontal'

export type DayOfWeekType = 0 | 1 | 2 | 3 | 4 | 5 | 6

type ReactRefType<ElementType> = {current: null | ElementType}

interface Props {
  containerRef?: ReactRefType<HTMLDivElement>
  selectedDate: string | null | undefined
  firstDayOfWeek: number
  maxMonthsCount?: number
  months: string[]
  numberOfMonthsToShow?: number
  onDayClick: (date: string) => void
  weekdaysShort: string[]
  showWeekDays?: boolean
  variant?: DatePickerVariant
  earliestCheckInDate?: Date
  hotelAvailabilityPrices: AvailabilityHotelEntity
  checkIn: string
  onMonthChange: (date: Date) => void
  showTotalPrices?: boolean
  searchComplete?: boolean
}

const getInitialMonth = (
  selectedDate: MiddayDate | null,
  variant: DatePickerVariant
) => {
  const todayDate = dateToMiddayDate(new Date())
  if (!selectedDate) {
    return todayDate
  }

  if (variant === 'vertical') {
    return selectedDate < todayDate ? selectedDate : todayDate
  }

  return selectedDate
}

const DatePickerComponent: React.FC<Props> = ({
  containerRef,
  selectedDate,
  weekdaysShort,
  months,
  firstDayOfWeek,
  numberOfMonthsToShow = 14,
  maxMonthsCount = 12,
  onDayClick,
  variant = 'horizontal',
  earliestCheckInDate = dateToMiddayDate(new Date()),
  hotelAvailabilityPrices,
  checkIn,
  onMonthChange,
  showWeekDays = false,
  showTotalPrices,
  searchComplete
}) => {
  const handleDayClick = (date: MiddayDate) => {
    const selectedDateDate = dateFormat(date, UTS_DATE_FORMAT)
    onDayClick(selectedDateDate)
  }

  const selectedDateDate = selectedDate
    ? dateStringToMiddayDate(selectedDate)
    : undefined
  const maxCheckInDate = subDays(
    addMonths(earliestCheckInDate, maxMonthsCount),
    1
  )

  const initialMonth = getInitialMonth(
    selectedDateDate || dateStringToMiddayDate(checkIn),
    variant
  )
  const fromMonth =
    variant === 'horizontal' ? earliestCheckInDate : initialMonth

  const toMonth = maxCheckInDate

  // FIXME: Days Of Week should be configurable based on Locale
  const modifiers: Record<string, Matcher | Matcher[]> = {
    startEnd: (day: Date) => isSameDay(day, selectedDateDate as Date),
    start: (day: Date) => isSameDay(day, selectedDateDate as Date),
    weekends: {dayOfWeek: [0, 6]},
    ...(selectedDateDate ? {selected: selectedDateDate} : {})
  }

  const disabledDays = {
    before: earliestCheckInDate
  }

  const CustomDayButton = useCallback(
    (props: DayButtonProps) => (
      <DayButton {...props}>
        <ColorPriceDayInner
          day={props.day.date}
          hotelAvailabilityPrices={hotelAvailabilityPrices}
          searchComplete={searchComplete}
          showTotalPrices={showTotalPrices}
          shouldShowPrices
        />
      </DayButton>
    ),
    [hotelAvailabilityPrices, searchComplete, showTotalPrices]
  )

  const CustomNextMonthButton = useCallback(
    (props: NextMonthButtonProps) => (
      <NextMonthButton {...props} data-id="next-month" />
    ),
    []
  )

  const CustomPreviousMonthButton = useCallback(
    (props: PreviousMonthButtonProps) => (
      <PreviousMonthButton {...props} data-id="previous-month" />
    ),
    []
  )

  const CustomMonthCaption = useCallback(
    (props: MonthCaptionProps) => {
      const {calendarMonth} = props
      const isCheckInMonth = selectedDateDate
        ? isSameMonth(calendarMonth.date, selectedDateDate)
        : false

      return (
        <MonthCaption {...props}>
          <MonthName
            months={months}
            isCheckInMonth={isCheckInMonth}
            date={calendarMonth.date}
          />
        </MonthCaption>
      )
    },
    [selectedDate, months, variant]
  )
  const formatWeekdayName: Formatters['formatWeekdayName'] = useCallback(
    (date: Date) => weekdaysShort[date.getDay()],
    [weekdaysShort]
  )

  const CustomWeekday = useCallback(
    (props: WeekdayProps) => {
      // In v9, WeekdayProps doesn't have a date property directly
      // We can determine the weekday by matching the formatted name (props.children)
      // to the weekdaysShort array index
      const weekdayName = String(props.children).trim()
      const weekdayIndex = weekdaysShort.findIndex(
        name => name.trim() === weekdayName
      )
      const isWeekend =
        weekdayIndex !== -1 && WEEKENDS_INDEXES.includes(weekdayIndex)
      return (
        <Weekday {...props}>
          <div className={cx('week-day-inner', isWeekend && '--is-weekend')}>
            {props.children}
          </div>
        </Weekday>
      )
    },
    [weekdaysShort]
  )

  return (
    <AvailabilityDatePickerContainer
      ref={containerRef}
      className={cx(
        variant === 'vertical' && '--is-vertical',
        '--is-single-month',
        showWeekDays && '--show-week-days'
      )}
    >
      <DayPicker
        locale={enUS}
        onDayClick={handleDayClick}
        classNames={{
          root: 'wrapper',
          day_button: 'day-cell',
          months: 'months-wrapper',
          month: 'month-wrapper',
          month_caption: 'caption-wrapper',
          caption_label: 'caption-label-wrapper',
          button_next: 'nav-button nav-button-next',
          button_previous: 'nav-button nav-button-previous',
          weekday: 'week-day-wrapper',
          month_grid: 'calendar-wrapper',
          weekdays: 'calendar-week-days-wrapper',
          selected: 'day-selected',
          disabled: 'day-disabled'
        }}
        modifiers={modifiers}
        disabled={disabledDays}
        defaultMonth={initialMonth} // Superfluous but required for test mocking
        startMonth={fromMonth}
        endMonth={toMonth}
        numberOfMonths={numberOfMonthsToShow}
        formatters={{
          formatWeekdayName
        }}
        weekStartsOn={firstDayOfWeek as DayOfWeekType}
        disableNavigation={variant !== 'horizontal'}
        onMonthChange={onMonthChange}
        components={{
          DayButton: CustomDayButton,
          MonthCaption: CustomMonthCaption,
          NextMonthButton: CustomNextMonthButton,
          PreviousMonthButton: CustomPreviousMonthButton,
          Chevron: ({orientation}) => (
            <Icon
              name={orientation === 'left' ? 'ChevronLeft' : 'ChevronRight'}
            />
          ),
          Weekday: CustomWeekday
        }}
        modifiersClassNames={{
          selected: 'selected',
          today: 'today',
          weekends: 'weekends',
          start: 'start',
          disabled: 'disabled',
          outside: 'outside',
          week: 'week',
          end: 'end'
        }}
      />
    </AvailabilityDatePickerContainer>
  )
}

export const AvailabilityDatePicker = React.memo(DatePickerComponent)
